/**
 * What happens to the outbox when the app comes back.
 *
 * Plan: `docs/plans/2026-09-08-next-steps-annotation-write-volume.md` §5 phase 3.
 *
 * OVERLAY, THEN DRAIN — and the order is not cosmetic. The Hub and the reader
 * paint from local state even offline, so a member who typed, was killed and
 * relaunched still offline would see the SERVER's version of their row, their
 * unsent edit invisible. The moment they typed on top of that, the new edit
 * would supersede the retained one and the words would be gone for good, with
 * nobody ever told. Putting the pending bodies back into the store before
 * anything paints means they are editing on top of their own text and supersede
 * is correct.
 *
 * THE OVERLAY IS A RULE, NOT A PATCH. Committing the pending bodies here only
 * helps a row that is already in Vuex, and for an enrolled member none are at
 * launch: their plaintext caches are off, and the Hub fills `allNotes` from the
 * corpus when it opens, replacing whatever was there. So each opened body is
 * also recorded in `pendingOverlay`, and the fetch mutations (`setAllNotes`,
 * `appendAllNotes`, `setPageHighlights`, `setBlankAnswers`) lay it over the
 * server's row for as long as the entry is still the outbox's to send.
 *
 * WHAT THE DRAIN NEVER TOUCHES: a key the live engine holds a slot for. The
 * outbox mirrors every live slot — `send` writes an entry before each attempt
 * and `pause` writes one for every waiting slot — so on `resume` and `online`
 * both the engine and this drain would otherwise send the same body, and the
 * second one earns a 409 — which this file answers by YIELDING, so the member's
 * own live edit would lose to their own replay. A key is owned by exactly one of
 * the two at a time; `drainAutosaveOutbox` filters the live ones out before
 * either function here sees them.
 *
 * THE IN-FLIGHT RECHECK. An entry is written before its request and removed
 * after, so a process killed in between leaves one marked `inFlight`. Resending
 * it blind is safe for a blank — the PUT is an idempotent upsert — but not for a
 * note, whose `expectedRevision` the first attempt may already have consumed;
 * that would surface as a 409 for an edit that in fact landed — and be yielded,
 * discarding the very body that landed. So an in-flight entry is checked against
 * the server first. It only ever runs after a crash mid-send, which is why an
 * extra GET there costs nothing in the ordinary case.
 */
import * as transport from '@/utils/courses-annotations-transport';
import { ANNOTATION_TYPE, isDeleteBlankAnswer } from '@/utils/courses-annotations-transport';

import {
  isStaleRevisionError,
  recordRevision,
} from '@/utils/courses-encryption/annotation-revisions';
import {
  SEALED_WRITE_ERRORS,
  isEncryptedWriteRequiredError,
  isSealedWriteError,
} from '@/utils/courses-encryption/sealed-writes';
import { AUTOSAVE_TYPE } from './types';
import { pendingOverlay } from './pending-overlay';
import { classify, isRetryable } from './policy';

/**
 * Whether this entry is ciphertext. A blank's sealed body IS the envelope; a
 * note's carries it as `payload`. A body that has neither belongs to a member
 * who is not enrolled — sealing is identity for them — and that is the entry
 * sign-out does not keep (decision 9.3).
 *
 * An emptied blank counts as sealed: it holds no words at all, so there is
 * nothing about it to keep out of the clear. It reaches disk as the delete
 * sentinel's fields rather than as `null` — see `isDeleteBlankAnswer`, which is
 * what this has to ask. Testing `sealed === null` alone read every queued
 * blank-clear as plaintext, so sign-out dropped it and T1's re-seal treated it
 * as a body to encrypt.
 */
export function isSealedEntry(entry) {
  if (!entry) return false;
  if (entry.sealed === null || entry.sealed === undefined) return true;
  if (isDeleteBlankAnswer(entry.sealed)) return true;
  return Boolean(entry.sealed.payload || entry.sealed.ct || entry.sealed.kid);
}

/**
 * The generation an entry was sealed under, wherever the envelope sits: a blank's
 * sealed body IS the envelope, a note's carries it as `payload`.
 */
export function envelopeKeyId(entry) {
  return entry?.sealed?.payload?.kid ?? entry?.sealed?.kid ?? null;
}

/** `program/lesson/block` back into the three things a blank write needs. */
export function blankScopeOf(itemId) {
  const parts = String(itemId || '').split('/');
  if (parts.length < 3) return null;
  const [programSlug, lessonSlug, ...rest] = parts;
  return { programSlug, lessonSlug, blockId: rest.join('/') };
}

/**
 * Put the pending bodies back on screen. Each entry is opened with the same
 * response hook a fetch would use, so a body sealed under a key this device no
 * longer holds simply does not appear — and the drain drops it below.
 *
 * @returns {Promise<number>} how many were applied
 */
export async function overlayOutbox({ entries, auth, store }) {
  let applied = 0;
  for (const entry of entries) {
    if (entry.type === AUTOSAVE_TYPE.NOTE) {
      try {
        const row = await transport.openNoteRow(auth, { id: entry.itemId, ...entry.sealed });
        if (!row?.body) continue;
        // A note is in two places — the Hub's flat list and, when it has one,
        // nested inside its highlight, which is where the drawer paints from.
        // The overlay runs before any fetch, so it cannot look up which
        // highlight; `patchNoteEverywhere` is the mutation for exactly that.
        const patch = { body: row.body, text: row.text ?? '', content: row.text ?? '' };
        pendingOverlay.set(entry.key, patch);
        store.commit('courses/patchNoteEverywhere', { noteId: entry.itemId, patch });
        applied += 1;
      } catch (error) {
        /* Cannot be opened — a dead key. The drain drops it below. */
      }
      continue;
    }
    if (entry.type !== AUTOSAVE_TYPE.BLANK) continue;
    const scope = blankScopeOf(entry.itemId);
    if (!scope) continue;
    try {
      // An emptied blank's pending state is "no answer" — there is no envelope
      // to open, and nothing to show but the empty field the member left.
      const answer =
        entry.sealed === null || isDeleteBlankAnswer(entry.sealed)
          ? ''
          : (await transport.openBlankRow(auth, scope, entry.sealed))?.answer;
      if (answer === undefined) continue;
      pendingOverlay.set(entry.key, { answer });
      store.commit('courses/setBlankAnswer', { blockId: scope.blockId, answer });
      if (entry.meta?.pageId) {
        store.commit('courses/setUserAnswer', {
          lessonId: scope.lessonSlug,
          pageId: entry.meta.pageId,
          blankId: scope.blockId,
          answer,
        });
      }
      applied += 1;
    } catch (error) {
      // Could not be opened — a dead key, most likely. Leave the screen alone
      // and let the drain decide what to do with the entry itself.
    }
  }
  return applied;
}

/**
 * Send what is waiting. Returns a small report so the caller can log or count
 * it rather than the outcome being invisible.
 *
 * @returns {Promise<{ sent: number, dropped: number, failed: number,
 *   yielded: number, droppedPlaintext: number, live: number, refused: number }>}
 */
export async function drainOutbox({
  entries,
  auth,
  outbox,
  memberId,
  keyIds = null,
  store = null,
  /** `true`, `false`, or `null` for "the status could not be read" (see `enrolmentForDrain`). */
  enrolled = false,
  /** Whether the live engine holds a slot for this key NOW — asked per entry, not once. */
  isLive = () => false,
}) {
  const report = {
    sent: 0,
    dropped: 0,
    failed: 0,
    yielded: 0,
    droppedPlaintext: 0,
    live: 0,
    /** Entries the server answered with a verdict, settled rather than replayed. */
    refused: 0,
  };
  // Reassigned by the re-seal below, which replaces the entry's body with the
  // envelope of it. Everything after that point reads the sealed form.
  for (let entry of entries) {
    // THE ROW WENT LIVE WHILE THIS LOOP WAS ON AN EARLIER ENTRY. The caller
    // filtered out every key the engine held when the drain began, but each
    // entry costs a round trip, and a member relaunching after a kill types
    // into exactly the row that was overlaid — the one they were last editing.
    // The moment they do, the engine owns the key: its slot has the newer body
    // and its own send will replace and remove this entry. Sending it here as
    // well would put two writes at one revision on the wire, and if the replay
    // were the one refused, `takeServerVersion` would paint the server's row
    // over what they are typing.
    if (isLive(entry.key)) {
      report.live += 1;
      continue;
    }
    // MARK LATE, CLEAR ON SETTLE, NEVER PRIME. A row is marked only once this
    // drain has actually tried to reach the server for it and could not, or
    // once it is waiting on something the member can see (approval from another
    // device). The one wait that resolves inside this launch without any
    // request — key hydration — stays silent, so a healthy launch flickers
    // nothing. Same rule the live engine follows (plan §6.5).
    //
    // Declared INSIDE the loop: they close over `entry`, which the re-seal
    // below reassigns mid-iteration. Optional all the way down because the
    // drain runs with `store = null`, and with store stubs that have no
    // `commit`, in the specs.
    const markFailed = error =>
      store?.commit?.('courses-autosave/setFailed', { key: entry.key, error });
    const clearFailed = () => store?.commit?.('courses-autosave/clearFailed', entry.key);
    // Sealed under a key this device no longer has — the member reset their
    // encryption, or signed every other device out, either of which makes the
    // old key unrecoverable. Sending it would store something nobody can open.
    // Whatever happens below except "left where it is", the entry stops being
    // the outbox's to send, and the overlay stops speaking for the row.
    const settle = async () => {
      pendingOverlay.clear(entry.key);
      await outbox.remove(memberId, entry.key);
      // Every path that takes the entry off the outbox comes through here —
      // sent, dropped, yielded, refused — so there is one place that takes the
      // "Not saved yet" line off the row with it.
      clearFailed();
    };
    // NOT KNOWN IS NOT "NO". A body queued in the clear may only go in the clear
    // once the device knows the member is not enrolled; `enrolled === null` says
    // the status could not be read this time (`enrolmentForDrain`). Sending it
    // anyway would put the member's words on the wire to be refused — the thing
    // the sealing hook refuses to do for a live write — so it waits for the
    // next drain, exactly as an offline send does. Envelopes are unaffected.
    if (enrolled === null && !isSealedEntry(entry)) {
      // REACHED ONLY AFTER THE STATUS READ FAILED, so it is an attempt, not a
      // wait: `lastRefreshedAt` is null at every launch, so for a member who
      // never set up encryption this branch IS the offline relaunch, and the
      // entry sits here with no send ever tried. Leaving it silent would leave
      // exactly the members #1802 is about with no line. No error object is in
      // scope, so a plain `{ code }` — `setFailed` reads `error?.code` for the
      // logged value and `UnsavedNotice` reads only presence.
      markFailed({ code: 'STATUS_UNREAD' });
      report.failed += 1;
      continue;
    }
    // THE MEMBER ENROLLED WHILE THIS WAS WAITING. The body was written in the
    // clear because sealing is a pass-through for a member who is not enrolled,
    // and the API now refuses a plaintext write from them (400
    // ENCRYPTED_WRITE_REQUIRED) — which the policy retries, so left alone it
    // would retry until the thirty-day sweep and the words would go in the end.
    //
    // It is not dropped, because it does not have to be: the plaintext IS the
    // entry, this device now holds the key, and the AAD binds an envelope to
    // member, type and item id, all three of which the entry carries. So it is
    // sealed now and sent as an envelope. Only a re-seal that FAILS drops the
    // entry, and the member is told once.
    if (enrolled && !isSealedEntry(entry)) {
      try {
        entry = { ...entry, sealed: await resealPlaintext(auth, entry) };
      } catch (error) {
        // "NOT YET" IS NOT "NEVER". The member is enrolled and this device holds
        // no key: it is on the approval screen waiting for another of the
        // member's phones, or a rotation is running elsewhere. Both end, and
        // the entry seals fine once they do — approval turns the key up, and
        // App.vue drains the moment it does. Dropping here lost the words for
        // exactly the phone T1 was written for: `pending` counts as enrolled,
        // so every resume while the approval screen was up threw this away.
        if (isTransientRefusal(error)) {
          // `NO_KEY` is the approval-screen wait, and it can last days, so the
          // member should see the row is not up. `ROTATION_IN_PROGRESS` rides
          // along: it needs a stranded plaintext entry during a rotation to
          // flicker at all, which is not worth a second helper.
          markFailed(error);
          report.failed += 1;
          continue;
        }
        // A deterministic refusal — a note whose revision this device never
        // read is the one that happens. Nothing later can make it sealable.
        await settle();
        report.droppedPlaintext += 1;
        continue;
      }
    }
    const verdict = keyVerdict(entry, keyIds);
    if (verdict === KEY_VERDICT.UNKNOWN) {
      // NOT MARKED: key hydration has not landed, nothing was asked of the
      // server, and `App.vue`'s `hasMasterKey` watch re-drains the moment it
      // does. A line here would flicker on every healthy launch.
      report.failed += 1;
      continue;
    }
    if (verdict === KEY_VERDICT.DEAD) {
      await settle();
      report.dropped += 1;
      continue;
    }
    const scope = entry.type === AUTOSAVE_TYPE.BLANK ? blankScopeOf(entry.itemId) : null;
    if (entry.type === AUTOSAVE_TYPE.BLANK && !scope) {
      await settle();
      report.dropped += 1;
      continue;
    }
    try {
      if (entry.inFlight && (await alreadyLanded({ entry, auth, scope }))) {
        await settle();
        report.sent += 1;
        continue;
      }
      await transport.sendSealed(auth, {
        type: entry.type,
        itemId: entry.itemId,
        sealed: entry.sealed,
        scope,
      });
      await settle();
      report.sent += 1;
    } catch (error) {
      // A REPLAY THAT LOST THE RACE YIELDS. This body is from a session that has
      // ended, with nobody looking at it, and the row has since been edited on
      // purpose somewhere else. Forcing it through would undo a deliberate edit
      // with an accidental one, and an envelope replaces the whole payload, so
      // there is no merge to fall back on. The entry goes and this device takes
      // the server's text.
      //
      // The live half of the rule is the opposite and lives in
      // `courses.js#forceThroughConflict`: a row the member is typing in
      // refetches and sends its text again. Nothing is shown to the member
      // either way — it is their own text on their own devices, and which
      // version wins is decided, not ambiguous.
      if (isStaleRevisionError(error)) {
        // FIRST, so that a refetch which throws cannot leave the entry to be
        // replayed — and refused — on every resume from here on.
        await settle();
        report.yielded += 1;
        if (store) {
          // `Promise.resolve`: Vuex answers an unknown action with `undefined`,
          // not a rejection, and a store without the module must not break the
          // rest of the drain.
          await Promise.resolve(
            store.dispatch('courses/takeServerVersion', {
              type: entry.type,
              itemId: entry.itemId,
              scope,
              origin: 'replay',
            }),
          ).catch(() => {});
        }
        continue;
      }
      // The server says the member is enrolled and this replay went up in the
      // clear: the device's status snapshot is stale (the member enrolled on
      // another phone since it was read). Nothing else on a keyless device
      // re-reads it, so the next drain would send the same plaintext again.
      // Re-read now; the next drain then re-seals the entry or holds it for
      // approval (NO_KEY) instead. Not awaited — the entry is left in place
      // below either way.
      if (isEncryptedWriteRequiredError(error) && store) {
        Promise.resolve(store.dispatch('courses-devices/refreshStatus')).catch(() => {});
      }
      // THE SAME TABLE THE LIVE ENGINE READS (`policy.js`). Offline, a 5xx, a
      // status not yet read, an enrolment not caught up with: left where it is,
      // and the next launch, resume or connectivity change tries again. A
      // VERDICT — the row is gone (404), the body is refused (400, 422), the
      // session is ending (401) — cannot change on the next attempt: this used
      // to leave those too, so a note deleted on another device was replayed
      // and refused on every resume for thirty days, until the age cap.
      if (isRetryable(classify(error))) {
        // The case #1802 is about: offline, or a 5xx. A send was tried and did
        // not land, so the row says so until a later drain settles it.
        markFailed(error);
        report.failed += 1;
        continue;
      }
      await settle();
      report.refused += 1;
    }
  }
  return report;
}

const KEY_VERDICT = Object.freeze({ USABLE: 'usable', DEAD: 'dead', UNKNOWN: 'unknown' });

/**
 * Whether the key a sealed entry was written under is one this device can still
 * open — DEAD when the list of readable keys is known and does not hold it (a
 * rotation or a reset on another device), USABLE when it does or the entry has
 * no key at all.
 *
 * NO KEYS IS NOT "EVERY KEY IS DEAD". An empty list is what App.vue builds when
 * key hydration has not landed or failed — the launch drain runs on `ready`'s
 * rejection too — and reading it as "this key is gone" dropped every queued
 * edit with the changed-key toast on a transient Keychain failure. Nothing can
 * be decided about a `kid` with no keys to compare it to, so it is UNKNOWN and
 * the entry waits, as a plaintext one does while the status is unknown; the
 * next drain — the key arriving, a resume — decides. `null` for the list means
 * "do not check", which is what specs pass.
 */
function keyVerdict(entry, keyIds) {
  const kid = envelopeKeyId(entry);
  if (!keyIds || !kid) return KEY_VERDICT.USABLE;
  if (!keyIds.length) return KEY_VERDICT.UNKNOWN;
  return keyIds.includes(kid) ? KEY_VERDICT.USABLE : KEY_VERDICT.DEAD;
}

/**
 * Seal a body that was queued before the member enrolled, under the key this
 * device holds now.
 *
 * SEED THE REVISION FIRST, or a note never gets past the hook. The revision map
 * is deliberately not persisted (`annotation-revisions.js`) — a number from a
 * previous launch is worthless against the row the server holds now — so after
 * a relaunch it is empty, and `sealRow` refuses an UPDATE whose revision it does
 * not know rather than send one the API would reject anyway. The entry has the
 * number: a plaintext note update carries `expectedRevision` in its stored body
 * whenever this device had read the row, because that is what `plaintextRevision`
 * put there. Recording it is exactly as true as it was when the entry was
 * written.
 *
 * An entry written WITHOUT one — the device had never read the row — cannot be
 * sealed and is dropped by the caller. That is the residue of this rule, and it
 * is smaller than it looks: a note is only edited from a screen that listed it.
 *
 * A BLANK IS SEEDED TOO, for a different reason: nothing refuses it, but a
 * blank write names the revision it read (T2) and the seal reads that from the
 * same map. Unseeded, a re-sealed answer would still carry the number — `split`
 * leaves `expectedRevision` in the clear fields — but a re-sealed CLEAR would
 * not: the delete path builds its sentinel from the map alone, so a clear
 * queued before enrolment went up unconditional and would have removed an
 * answer typed since on another device. Seeding both makes the two paths agree
 * on purpose rather than one of them by accident.
 */
async function resealPlaintext(auth, entry) {
  const type = entry.type === AUTOSAVE_TYPE.NOTE ? ANNOTATION_TYPE.NOTE : ANNOTATION_TYPE.BLANK;
  recordRevision(type, entry.itemId, entry.sealed?.expectedRevision);
  return transport.sealAnnotation(auth, entry.type, entry.itemId, entry.sealed);
}

/**
 * A sealing refusal that a later drain can succeed past: this device does not
 * hold the key YET. Every other `SealedWriteError` is a verdict on the entry.
 */
function isTransientRefusal(error) {
  return (
    isSealedWriteError(error, SEALED_WRITE_ERRORS.NO_KEY) ||
    isSealedWriteError(error, SEALED_WRITE_ERRORS.ROTATION_IN_PROGRESS)
  );
}

/**
 * Did the request this entry was killed in the middle of actually land? Only
 * asked for an entry still marked in-flight.
 */
async function alreadyLanded({ entry, auth, scope }) {
  if (entry.type === AUTOSAVE_TYPE.NOTE) {
    // A note's envelope names the revision it was based on, and the server bumps
    // that on every accepted write. So "has the row moved past what we aimed
    // at?" answers this exactly, where comparing bodies could not. It matters
    // more here than for a blank: resending a note that already landed earns a
    // 409, and 9.2 would turn that into a duplicate the member never asked for.
    try {
      const row = await transport.fetchNoteByIdRaw(auth, entry.itemId);
      const base = entry.sealed?.expectedRevision;
      if (!row || typeof base !== 'number') return false;
      return typeof row.revision === 'number' && row.revision > base;
    } catch (error) {
      return false;
    }
  }
  if (entry.type !== AUTOSAVE_TYPE.BLANK) return false;
  try {
    const rows = await transport.fetchBlankAnswers(auth, {
      slug: scope.lessonSlug,
      programSlug: scope.programSlug,
    });
    const row = (rows || []).find(r => r.blockId === scope.blockId);
    // A queued clear landed if the row is gone.
    if (entry.sealed === null || isDeleteBlankAnswer(entry.sealed)) return !row;
    // The answer cannot be compared through the envelope (a fresh IV every
    // time), so "a row exists where the write would have made one" is as far as
    // this can honestly go. A blank PUT is an idempotent upsert, so resending
    // when this is wrong costs one extra request and nothing else.
    return Boolean(row) && row.updatedAt && new Date(row.updatedAt).getTime() >= entry.enqueuedAt;
  } catch (error) {
    return false;
  }
}
